/**
 * SEO component that queries for data with
 *    Gatsby's useStaticQuery React hook
 *
 * See: https://www.gatsbyjs.org/docs/use-static-query/
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { useStaticQuery, graphql } from 'gatsby';

/**
 * This is part of the default Gatsby site, it's been extended a little to deal
 * with items in the head, not just SEO. Also includes scripts for GTM and Hotjar
 *
 * @function
 * @param       {string} description optional description. If empty, use the description at the start of the
 * `gatsby-config.js` siteMetaData block.
 * @param       {string} lang        language of the site, eg 'en'
 * @param       {array}  meta        array of additional meta items
 * @param       {string} title       page title.
 * @constructor
 */
function SEO({ description, lang, meta, title, market }) {
    const { site } = useStaticQuery(
        graphql`
            query {
                site {
                    siteMetadata {
                        title
                        siteUrl
                        description
                        author
                        img
                        favIcon
                    }
                }
            }
        `
    );

    const metaDescription = description || site.siteMetadata.description;

    return (
        <Helmet
            htmlAttributes={{
                lang
            }}
            title={title}
            titleTemplate={`%s | ${site.siteMetadata.title}`}
            meta={[
                {
                    name: 'description',
                    content: metaDescription
                },
                {
                    property: 'og:title',
                    content: title
                },
                {
                    property: 'og:description',
                    content: metaDescription
                },
                {
                    property: 'og:type',
                    content: 'website'
                },
                {
                    property: 'og:url',
                    content: site.siteMetadata.siteUrl
                },
                {
                    property: 'og:image',
                    content: site.siteMetadata.img
                },
                {
                    name: 'twitter:card',
                    content: 'summary'
                },
                {
                    name: 'twitter:creator',
                    content: site.siteMetadata.author
                },
                {
                    name: 'twitter:title',
                    content: title
                },
                {
                    name: 'twitter:description',
                    content: metaDescription
                }
            ].concat(meta)}
            link={[
                {
                    rel: 'shortcut icon',
                    type: 'image/png',
                    href: site.siteMetadata.favIcon
                },
                {
                    rel: 'preconnect',
                    href: 'https://www.sc.pages06.net'
                },
                {
                    rel: 'preconnect',
                    href: 'https://www.pages06.net'
                }
            ]}
        />
    );
}

SEO.defaultProps = {
    lang: 'en',
    meta: [],
    description: ''
};

SEO.propTypes = {
    description: PropTypes.string,
    lang: PropTypes.string,
    meta: PropTypes.arrayOf(PropTypes.object),
    title: PropTypes.string.isRequired,
    market: PropTypes.string.isRequired
};

export { SEO };
